import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Loader2, CheckCircle2, XCircle, Send, FileText, Calendar, User, Mail } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

export default function AgentFollowupPage() {
  const { token } = useParams();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [show, setShow] = useState(null); // true | false | null
  const [proposal, setProposal] = useState('');
  const [transcript, setTranscript] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) return;
    axios
      .get(`${BACKEND}/api/public/agent-followup/${token}`)
      .then(({ data }) => {
        setState(data);
        if (data.status === 'completed') setDone(true);
      })
      .catch(() => toast.error('Link non valido o scaduto.'))
      .finally(() => setLoading(false));
  }, [token]);

  const submit = async () => {
    if (show === null) {
      toast.error('Indica se il lead ha partecipato');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND}/api/public/agent-followup/${token}`, {
        show,
        proposal_text: proposal.trim() || null,
        transcript: transcript.trim() || null,
      });
      setDone(true);
      toast.success('Follow-up inviato — admin notificato');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore invio');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-[var(--surface-1)]">
        <Loader2 className="w-8 h-8 animate-spin text-[var(--gp-gold)]" />
      </div>
    );
  }

  if (!state) {
    return (
      <div className="min-h-screen grid place-items-center bg-[var(--surface-1)] p-6">
        <div className="max-w-md text-center">
          <XCircle className="w-12 h-12 text-red-500 mx-auto" />
          <h1 className="font-serif text-2xl mt-3">Link non valido</h1>
          <p className="text-sm text-muted-foreground mt-2">Questo link di follow-up è scaduto o non valido.</p>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen grid place-items-center bg-[var(--surface-1)] p-6">
        <div className="max-w-md text-center bg-white rounded-2xl shadow-xl p-8 border">
          <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto" />
          <h1 className="font-serif text-3xl text-[hsl(var(--primary))] mt-4">Grazie!</h1>
          <p className="text-sm text-muted-foreground mt-3">
            Il follow-up è stato registrato. Il team admin riceve subito la notifica con tutte le informazioni.
          </p>
          <p className="text-xs text-muted-foreground mt-4">Puoi chiudere questa pagina.</p>
        </div>
      </div>
    );
  }

  const callDate = state.call_at ? new Date(state.call_at) : null;

  return (
    <div className="min-h-screen bg-[var(--surface-1)] py-8 px-4" data-testid="agent-followup-page">
      <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow-xl border overflow-hidden">
        <div className="px-6 md:px-8 py-5 bg-[var(--gp-forest)] text-white">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--gp-gold-soft,#E8C97A)]">Follow-up call</div>
          <h1 className="font-serif text-2xl md:text-3xl mt-1">Compila in 2 minuti</h1>
          <p className="text-xs md:text-sm opacity-80 mt-2">
            Le tue risposte vengono inviate automaticamente all'admin (Richard + team) e attivano il flusso di follow-up corretto.
          </p>
        </div>

        <div className="p-6 md:p-8 space-y-5">
          {/* Lead & agent metadata */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <div className="bg-[var(--surface-2)] rounded-lg p-3">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Lead</div>
              <div className="font-semibold text-[hsl(var(--primary))] flex items-center gap-2"><User className="w-3.5 h-3.5" />{state.lead?.name || '—'}</div>
              <div className="text-xs text-muted-foreground flex items-center gap-1 mt-1"><Mail className="w-3 h-3" />{state.lead?.email || '—'}</div>
              {state.lead?.countries?.length > 0 && (
                <div className="text-[11px] text-muted-foreground mt-1">
                  {state.lead.countries.join(', ')} · {(state.lead.path || '').replace('_', ' ')}
                </div>
              )}
            </div>
            <div className="bg-[var(--surface-2)] rounded-lg p-3">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Agente</div>
              <div className="font-semibold text-[hsl(var(--primary))]">{state.agent?.name || '—'}</div>
              {callDate && (
                <div className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                  <Calendar className="w-3 h-3" /> {callDate.toLocaleString('it-IT', { dateStyle: 'medium', timeStyle: 'short' })}
                </div>
              )}
            </div>
          </div>

          {/* Q1 — Show / No-show */}
          <div>
            <Label className="text-sm font-semibold">1. Il lead ha partecipato alla call?</Label>
            <div className="grid grid-cols-2 gap-3 mt-2">
              <button
                type="button"
                onClick={() => setShow(true)}
                className={`p-4 rounded-lg border-2 transition-colors ${
                  show === true ? 'border-emerald-500 bg-emerald-50' : 'border-border hover:border-emerald-300'
                }`}
                data-testid="show-yes"
              >
                <CheckCircle2 className="w-6 h-6 text-emerald-500 mx-auto" />
                <div className="font-semibold mt-1">Sì, ha partecipato</div>
                <div className="text-[11px] text-muted-foreground">Show</div>
              </button>
              <button
                type="button"
                onClick={() => setShow(false)}
                className={`p-4 rounded-lg border-2 transition-colors ${
                  show === false ? 'border-red-500 bg-red-50' : 'border-border hover:border-red-300'
                }`}
                data-testid="show-no"
              >
                <XCircle className="w-6 h-6 text-red-500 mx-auto" />
                <div className="font-semibold mt-1">No, no-show</div>
                <div className="text-[11px] text-muted-foreground">Trigger flusso recovery</div>
              </button>
            </div>
          </div>

          {/* Q2 — Proposal */}
          <div>
            <Label className="text-sm font-semibold flex items-center gap-2"><FileText className="w-4 h-4" /> 2. Hai inviato una proposta? Incolla il testo.</Label>
            <textarea
              value={proposal}
              onChange={(e) => setProposal(e.target.value)}
              rows={5}
              placeholder="Incolla qui il testo della proposta inviata al lead (es. quote, paesi, timeline, anticipo, condizioni...).&#10;Se non hai mandato la proposta, lascia vuoto."
              className="w-full mt-2 p-3 border rounded-md text-sm bg-background focus:ring-2 focus:ring-[var(--gp-gold)]/50 focus:border-[var(--gp-gold)]"
              data-testid="proposal-text"
            />
          </div>

          {/* Q3 — Transcript */}
          <div>
            <Label className="text-sm font-semibold flex items-center gap-2">3. Trascrizione meeting</Label>
            <textarea
              value={transcript}
              onChange={(e) => setTranscript(e.target.value)}
              rows={8}
              placeholder="Incolla la trascrizione automatica del meeting (da Zoom AI Companion, Otter.ai, Fireflies, ecc.).&#10;Anche un riassunto a punti va bene."
              className="w-full mt-2 p-3 border rounded-md text-sm bg-background focus:ring-2 focus:ring-[var(--gp-gold)]/50 focus:border-[var(--gp-gold)] font-mono text-xs"
              data-testid="transcript-text"
            />
          </div>

          <Button
            onClick={submit}
            disabled={submitting || show === null}
            className="w-full h-12 bg-[var(--gp-gold)] text-[var(--gp-forest)] hover:bg-[var(--gp-gold-soft,#E8C97A)] font-semibold text-base"
            data-testid="submit-followup"
          >
            {submitting ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Send className="w-5 h-5 mr-2" />}
            Invia follow-up
          </Button>
        </div>
      </div>
    </div>
  );
}
